import React, { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { imageApi, fileToBase64, errorMessage } from "../../lib/api";

// Image URL field with optional upload to the backend image store.
export default function ImageField({ value, onChange, testId = "image-field" }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png)$/.test(file.type)) return toast.error("Menu photos must be JPG or PNG");
    if (file.size > 10 * 1024 * 1024) return toast.error("Please choose an image under 10 MB");
    setBusy(true);
    try {
      const data = await fileToBase64(file);
      const res = await imageApi.upload(data, file.type || "image/jpeg");
      const url = res.url || (res.image_id ? imageApi.url(res.image_id) : null);
      if (!url) throw new Error("Upload did not return an image");
      onChange(url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(errorMessage(e, "Upload failed")); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="flex gap-3">
      <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-xl border border-surface-300 bg-surface-100">
        {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center text-ink-muted"><ImagePlus className="h-5 w-5" /></span>}
        {value && <button type="button" onClick={() => onChange("")} className="absolute right-1 top-1 rounded-full bg-ink/70 p-0.5 text-white" aria-label="Remove image"><X className="h-3 w-3" /></button>}
      </div>
      <div className="flex-1 space-y-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste an image URL" data-testid={`${testId}-url`} />
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-brand hover:underline">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} {busy ? "Uploading…" : "or upload a photo (JPG/PNG)"}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-file`} />
        </label>
      </div>
    </div>
  );
}
